import {injectable} from "inversify";
import Scene = Phaser.Scene;
import SoundUtils from "@/utils/sound-utils";
import {Keys} from "@/constants/key";
import {TabbedPanel} from "@/scenes.ui/tabbed-panel";
import {SpaceTr, Tr, TrSpace} from "@/i18n/i18n";
import PlayerStore from "@/store/player";
import {OfferType, PlayerMarker} from "@/store/player/types";
import ShopStore from "@/store/shop";
import {HeroExchangeOptions, ShopCreature} from "@/store/shop/types";
import {ShopCreaturePanel} from "@/scenes.ui/shop-creature-panel";
import {CreatureUtils} from "@/utils/creature-utils";
import BaseSound = Phaser.Sound.BaseSound;
import {BlackoutUtils} from "@/utils/blackout-utils";
import {SellCreaturePanel} from "@/scenes.ui/sell-creature-panel";
import {MythHeroPanel} from "@/scenes.ui/myth-hero-panel";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {BuyCreatureConfirmationPanel} from "@/scenes.ui/buy-creature-confirmation-panel";
import {Settings} from "@/constants/settings";
import {NewCreaturePanel} from "@/scenes.ui/new-creature-panel";
import {InfoPanel} from "@/scenes.ui/info-panel";
import {GameState} from "@/constants/game-state";
import {MythIntroPanel} from "@/scenes.ui.clan/myth-intro-panel";
import {Markers} from "@/constants/marker";
import {ListTextPanel} from "@/scenes.ui/list-text-panel";
import Container = Phaser.GameObjects.Container;
import {HeroIntroPanel} from "@/scenes.ui.clan/hero-intro-panel";
import {TopIntroPanel} from "@/scenes.ui.clan/top-intro-panel";
import {ValuteHeroPanel} from "@/scenes.ui/valute-hero-panel";
import ShopUtils from "@/utils/shop-utils";
import {IntegrationUtils} from "@/utils/integration-utils";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {IapService} from "@/services/iap-service";
import {PaymentChangeService} from "@/services/payment-change-service";

@injectable()
export class HeroShopService {

    private scene!: Scene;
    private canvas!: HTMLCanvasElement;
    private tabbedPanel!: TabbedPanel;

    private awardSound!: BaseSound | undefined;
    private selectSound!: BaseSound | undefined;
    private levelUpSound!: BaseSound | undefined;
    private buySound!: BaseSound | undefined;

    private firstOpen!: boolean;

    private closeCallback!: () => unknown;

    private statClickCallback!: (creatureId: string) => unknown;

    private paymentNotificationService!: PaymentNotificationService;
    private iapService!: IapService;
    private paymentChangeService!: PaymentChangeService;

    public openShop(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => unknown,
                    statClickCallback: (creatureId: string) => unknown,
                    paymentNotificationService: PaymentNotificationService, iapService: IapService, paymentChangeService: PaymentChangeService) {
        let self = this;
        this.scene = scene;
        this.canvas = canvas;
        this.firstOpen = true;
        this.closeCallback = closeCallback;
        this.statClickCallback = statClickCallback;
        this.paymentNotificationService = paymentNotificationService;
        this.iapService = iapService;
        this.paymentChangeService = paymentChangeService;

        self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
        self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
        self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);
        self.buySound = SoundUtils.addSound(self.scene!, Keys.SOUND_BUTTON_CLICK);

        let player = PlayerStore.getPlayer();
        let st = player.bestVictoryStair;
        self.tabbedPanel = new TabbedPanel(scene!, canvas!, Tr('h.shop'), ['usual', 'legend', 'myth', 'top'],
            [Tr('h.usual'), Tr('h.legend'), Tr('h.myth'), Tr('h.top')],
            [true, st >= Settings.LEGEND_HERO_MIN_STAIR, st >= Settings.MYTH_HERO_MIN_STAIR, st >= Settings.TOP_HERO_MIN_STAIR], self.selectSound, (tabId: string) => {
                if (tabId === 'usual') {
                    this.populate('usual');
                } else if (tabId === 'legend') {
                    this.populate('legend');
                } else if (tabId === 'myth') {
                    this.populate('myth');
                } else if (tabId === 'top') {
                    this.populate('top');
                }
            }, (tabId: string) => {
                if (tabId === 'legend') {
                    let ip: InfoPanel = new InfoPanel(scene, canvas, Tr('ups'), Tr('her.unavailable'),
                        TrSpace('min.lev.legend') + Settings.LEGEND_HERO_MIN_STAIR + SpaceTr('lev.gen'), () => {});
                    ip.setDepth(6000);
                    ip.openAnimated();
                } else if (tabId === 'myth') {
                    let ip: InfoPanel = new InfoPanel(scene, canvas, Tr('ups'), Tr('her.unavailable'),
                        TrSpace('min.lev.myth') + Settings.MYTH_HERO_MIN_STAIR + SpaceTr('lev.gen'), () => {});
                    ip.setDepth(6000);
                    ip.openAnimated();
                } else if (tabId === 'top') {
                    let ip: InfoPanel = new InfoPanel(scene, canvas, Tr('ups'), Tr('her.unavailable'),
                        TrSpace('min.lev.top') + Settings.TOP_HERO_MIN_STAIR + SpaceTr('lev.gen'), () => {});
                    ip.setDepth(6000);
                    ip.openAnimated();
                }
            }, closeCallback);

        let tab = HeroShopService.getDefaultTab(st);
        self.tabbedPanel.goToTab(tab);

        if (tab === 'myth' && !this.haveMarker(Markers.MYTH_SEEN_MARKER)) {
            this.scene.time.delayedCall(700, () =>{
               let introPanel: HeroIntroPanel = new MythIntroPanel(scene, canvas);
               introPanel.openAnimated();
                PlayerStore.setMarker(Markers.MYTH_SEEN_MARKER);
            });
        } else if (tab === 'top' && !this.haveMarker(Markers.TOP_SEEN_MARKER)) {
            this.scene.time.delayedCall(700, () =>{
                let introPanel: HeroIntroPanel = new TopIntroPanel(scene, canvas);
                introPanel.openAnimated();
                PlayerStore.setMarker(Markers.TOP_SEEN_MARKER);
            });
        }
    }

    private haveMarker(marker: String): Boolean {
        return PlayerStore.getPlayer().playerMarkers.some((m: PlayerMarker) => m.playerMarker == marker);
    }

    private static getDefaultTab(stair: number): string {
        if (stair >= Settings.MYTH_HERO_MIN_STAIR) {
            return 'myth';
        } else if (stair >= Settings.LEGEND_HERO_MIN_STAIR) {
            return 'legend';
        }
        return 'usual';
    }

    private populate(tabId: string): void {
        let self = this;

        let player = PlayerStore.getPlayer()!;
        if (player.offers) {
            for (let offer of player.offers) {
                if (offer.offerType === OfferType.FIRST_HERO) {
                    this.tabbedPanel.setAgitationText(Tr('skidka.na.pervogo.geroja'));
                } else if (offer.offerType === OfferType.ALL_CREATURES_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('skidki.na.vseh.geroev'));
                } else if (offer.offerType === OfferType.GREEN_DRAGON_DISCOUNT || offer.offerType === OfferType.BLACK_DRAGON_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('unikalniy.drakon.so.skidkoy'));
                } else if (offer.offerType === OfferType.BRAND_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('br.camp'));
                } else {
                    this.tabbedPanel.setAgitationText(Tr('uvelichte.svoy.otrjad.vplot.do..geroev'));
                }
            }
        }

        let init = (eo: HeroExchangeOptions) => {
            const shopCreatures: ShopCreature[] = eo.shopCreatures;
            let panels: Container[] = [];

            panels.push(...HeroShopService.getTopTextsByTabId(this.scene!, this.canvas!));

            let panelY = 0;
            shopCreatures.forEach(sc => {
                let classText = CreatureUtils.getClassName(sc.creature.warriorClass);
                if (!classText) {
                    return;
                }

                let scp: ShopCreaturePanel;

                if (tabId === 'myth' || tabId === 'hero-town' || tabId === 'titan-town') {
                    scp = new MythHeroPanel(this.scene!, 0, panelY, sc, self.statClickCallback, () => {
                        let sellPanel: SellCreaturePanel = new SellCreaturePanel(self.scene, self.canvas, sc,
                            eo.myCreaturePrices, self.buySound, (checkedIds: number[], price: number) => {
                                sellPanel.close();
                                if (PlayerStore.getPlayer().mithril < price) {
                                    SoundUtils.play(self.levelUpSound);
                                    this.tabbedPanel.goToTab('bank');
                                    new FeedbackPanel(self.scene!, Tr('ne.hvataet.izumrudov'), self.tabbedPanel!, false).show();
                                } else {
                                    GameState.REQUEST_REFRESH_NAV_PANEL = true;
                                    ShopStore.exchangeCreature(sc.id, checkedIds).then(() => {
                                        this.tabbedPanel.closePanel();
                                        SoundUtils.play(self.awardSound);
                                        sc.creatures.forEach(c => {
                                            new NewCreaturePanel(self.scene!, self.canvas!, c).openAnimated();
                                        });
                                    });
                                }
                        });
                        sellPanel.openAnimated();
                    });
                } else {
                    let callb = () => {
                        if (PlayerStore.getPlayer().mithril < sc.price) {
                            SoundUtils.play(self.levelUpSound);
                            this.tabbedPanel.goToTab('bank');
                            new FeedbackPanel(self.scene!, Tr('ne.hvataet.izumrudov'), self.tabbedPanel!, false).show();
                        } else {

                            let isSquadFull = CreatureUtils.findNonTemporalCreatures(PlayerStore.getPlayer().creatures).length >= Settings.MAX_CREATURES;

                            let confirmPanel: BuyCreatureConfirmationPanel = new BuyCreatureConfirmationPanel(this.scene!, this.canvas!,
                                this.tabbedPanel!.depth + 1, sc, () => {
                                    let bo = sc.bankOption;

                                    self.tabbedPanel.closePanel();
                                    SoundUtils.play(self.awardSound);

                                    if (bo) {
                                        ShopUtils.buy(undefined, self.awardSound, player.userSource,
                                            self.paymentNotificationService, self.iapService, self.paymentChangeService, bo, IntegrationUtils.isExternalUser());
                                    } else {
                                        ShopStore.buyCreature(sc.id).then(() => {
                                            new NewCreaturePanel(self.scene!, self.canvas!, sc.creature).open();
                                        });
                                    }
                                }, isSquadFull, Settings.MAX_CREATURES);
                            confirmPanel.open();
                        }
                    };
                    if (sc.bankOption) {
                        scp = new ValuteHeroPanel(this.scene!, 0, panelY, sc, self.statClickCallback, callb);
                    } else {
                        scp = new ShopCreaturePanel(this.scene!, 0, panelY, sc, self.statClickCallback, callb);
                    }
                }

                panels.push(scp);
                panelY += ShopCreaturePanel.HEIGHT;
            });

            panels.push(...HeroShopService.getTextsByTabId(this.scene!, this.canvas!, tabId));

            self.tabbedPanel.supplyChildren(tabId, panels);
        };

        if (self.firstOpen) {
            self.firstOpen = false;
            let blackouted = false;
            let options: HeroExchangeOptions | undefined = undefined;

            BlackoutUtils.blackout(self.scene, self.canvas, () => {
                self.scene.add.existing(self.tabbedPanel);
                blackouted = true;
                if (options) {
                    init(options);
                }
            });

            ShopStore.fetchHeroExchangeOptions(tabId).then((eo: HeroExchangeOptions) => {
                options = eo;
                if (blackouted) {
                    init(eo);
                }
            });
        } else {
            ShopStore.fetchHeroExchangeOptions(tabId).then((eo: HeroExchangeOptions) => {
                init(eo);
            });
        }
    }

    public static getTopTextsByTabId(scene: Scene, canvas: HTMLCanvasElement): Container[] {
        let res: ListTextPanel[] = [];

        // res.push(new ListNewsPanel(scene, canvas, Tr('june-3'),
        //     Tr('det'), 'june_exchange_rules', true));

        return res;
    }

    private static getTextsByTabId(scene: Scene, canvas: HTMLCanvasElement, tabId: string): Container[] {
        let res: ListTextPanel[] = [];

        if (tabId === 'usual') {
            res.push(new ListTextPanel(scene, Tr('us.inf.1'), 40));
            res.push(new ListTextPanel(scene, Tr('us.inf.2'), 70));
        } else if (tabId === 'legend') {
            res.push(new ListTextPanel(scene, Tr('leg.inf.1'), 60));
        } else if (tabId === 'myth') {
            res.push(new ListTextPanel(scene, Tr('myth.inf.1'), 60));
            res.push(new ListTextPanel(scene, Tr('myth.inf.2'), 60));
            //res.push(new ListManualLink(scene, canvas, 'test', 'Подробнее', 60));
        } else if (tabId === 'top') {
            res.push(new ListTextPanel(scene, Tr('top.inf.1'), 100));
        }

        return res;
    }
}
